import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, CalendarCheck, Mail, Leaf } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Overline } from "@/components/Reveal";
import api from "@/api";
import { FALLBACK_TREATMENTS, PACKAGES, SITE } from "@/config/site";

const Trattamenti = () => {
    const [treatments, setTreatments] = useState(FALLBACK_TREATMENTS);

    useEffect(() => {
        api.get("/treatments")
            .then((res) => setTreatments(res.data.treatments))
            .catch(() => {});
    }, []);

    return (
        <div data-testid="trattamenti-page">
            <SEO
                title="Trattamenti e Massaggi Olistici a Bologna | Sara Solferini"
                description="Massaggio rilassante, decontratturante, olistico e trattamenti personalizzati a Bologna. Prenota online il tuo momento di benessere."
            />

            <header className="mx-auto max-w-6xl px-5 pb-16 pt-36 md:px-8 md:pt-44">
                <Reveal>
                    <Overline>Trattamenti</Overline>
                    <h1 className="mt-5 max-w-2xl font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="trattamenti-title">
                        Ogni corpo ha una storia. <span className="italic text-sage">Ogni trattamento la ascolta.</span>
                    </h1>
                    <p className="mt-7 max-w-xl text-base leading-relaxed text-clay">
                        Sette percorsi di benessere, un unico filo conduttore: ogni gesto è pensato per
                        accompagnarti verso un equilibrio profondo. Scegli in base a ciò che senti, non solo
                        a ciò di cui credi di aver bisogno.
                    </p>
                </Reveal>
            </header>

            <section className="mx-auto max-w-6xl space-y-20 px-5 pb-24 md:px-8" data-testid="trattamenti-list">
                {treatments.map((t, i) => (
                    <Reveal key={t.id}>
                        <article
                            className={`grid items-center gap-10 md:grid-cols-2 md:gap-16 ${
                                i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                            }`}
                            data-testid={`trattamento-${t.id}`}
                        >
                            <div className="overflow-hidden rounded-2xl">
                                <img
                                    src={t.image}
                                    alt={t.name}
                                    loading="lazy"
                                    className={`w-full transition-transform duration-700 hover:scale-105 ${
                                        t.id === "personalizzato"
                                            ? "h-auto"
                                            : "h-72 object-cover md:h-[440px]"
                                    }`}
                                />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.25em] text-sage">{t.tagline}</p>
                                <h2 className="mt-3 font-serif text-3xl font-normal text-ink sm:text-4xl">{t.name}</h2>
                                <p className="mt-5 text-base leading-relaxed text-clay">{t.description}</p>
                                <div className="mt-7 flex flex-wrap items-center gap-3 text-sm text-clay">
                                    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
                                        <Clock className="h-4 w-4 text-sage" strokeWidth={1.5} />
                                        <span data-testid={`durata-${t.id}`}>{t.duration}</span>
                                    </span>
                                    <span className="rounded-full border border-border bg-card px-4 py-2" data-testid={`prezzo-${t.id}`}>
                                        {t.price}
                                    </span>
                                    {t.price.includes("——") && (
                                        <span className="text-xs italic text-fog">listino in aggiornamento</span>
                                    )}
                                </div>
                                <Link
                                    to={`/prenota?trattamento=${t.id}`}
                                    data-testid={`btn-prenota-${t.id}`}
                                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-sage px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark"
                                >
                                    Prenota
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                                </Link>
                            </div>
                        </article>
                    </Reveal>
                ))}
            </section>

            <section className="mx-auto max-w-6xl px-5 pb-24 md:px-8" data-testid="pacchetti-section">
                <Reveal>
                    <div className="rounded-3xl bg-sage-light/60 p-10 text-center md:p-14">
                        <Overline className="justify-center">Pacchetti</Overline>
                        <h2 className="mt-4 font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl">
                            Percorsi di 4 incontri, <span className="italic text-sage">scontati del 10%</span>
                        </h2>
                        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-clay">
                            Per chi desidera un percorso continuativo: ogni pacchetto comprende 4 incontri
                            da 60 minuti dello stesso trattamento, con il 10% di sconto.
                        </p>
                        <p className="mt-5 text-sm font-semibold uppercase tracking-widest text-sage-dark" data-testid="pacchetti-validity">
                            La validità del pacchetto è di 1 mese
                        </p>
                    </div>
                </Reveal>
                <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="pacchetti-grid">
                    {PACKAGES.map((p, i) => (
                        <Reveal key={p.id} delay={i * 0.06}>
                            <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-[0_4px_20px_rgba(67,46,30,0.05)]" data-testid={`pacchetto-${p.id}`}>
                                <p className="text-[11px] uppercase tracking-[0.22em] text-sage">4 incontri · 60 min</p>
                                <h3 className="mt-2 font-serif text-xl leading-snug text-ink">{p.name}</h3>
                                <div className="mt-4 flex items-baseline gap-2">
                                    <span className="text-sm text-fog line-through">{p.original}</span>
                                    <span className="font-serif text-2xl text-sage-dark">{p.price}</span>
                                </div>
                                <a
                                    href={`https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(`Buongiorno, vorrei informazioni sul pacchetto 4 incontri: ${p.name}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`pacchetto-cta-${p.id}`}
                                    className="mt-5 inline-block rounded-full border border-sage px-5 py-2.5 text-center text-xs font-semibold uppercase tracking-widest text-sage transition-colors duration-300 hover:bg-sage hover:text-cream"
                                >
                                    Prenota il pacchetto
                                </a>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-sand py-20" data-testid="trattamenti-come-funziona">
                <div className="mx-auto max-w-6xl px-5 md:px-8">
                    <Reveal>
                        <Overline>Come funziona</Overline>
                    </Reveal>
                    <div className="mt-10 grid gap-8 md:grid-cols-3">
                        {[
                            { icon: Leaf, title: "Scegli", text: "Seleziona il trattamento più adatto al momento che stai vivendo." },
                            { icon: CalendarCheck, title: "Prenota", text: "Scegli data e orario online, con almeno 1 giorno di preavviso." },
                            { icon: Mail, title: "Ricevi conferma", text: "Una email automatica riassume il tuo appuntamento. Lo studio conferma personalmente." },
                        ].map((step, i) => (
                            <Reveal key={step.title} delay={i * 0.1}>
                                <div className="rounded-2xl bg-card p-8 shadow-[0_4px_20px_rgba(67,46,30,0.05)]" data-testid={`step-${i + 1}`}>
                                    <step.icon className="h-7 w-7 text-sage" strokeWidth={1.5} />
                                    <h3 className="mt-5 font-serif text-2xl text-ink">{step.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-clay">{step.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Trattamenti;
